import { observer } from 'mobx-react';
import { useEffect, useState, useRef } from 'react'; 
import { useStore } from '../../core/utils/hook';
import { useForm } from 'react-hook-form';
import yup from '../../core/utils/yupValidate';
import { yupResolver } from '@hookform/resolvers/yup';
import { LOGIN_TYPE, SYSTEM_PATH } from '../../core/configs/constants';
import { consumeRedirect } from '../../core/utils/loginRedirect';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import OtpInput from 'react-otp-input';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import ResignedConfirmModal from './ResignedConfirmModal';
import ChangeEmail from './ChangeEmail';
import { flowResult } from 'mobx';

const LoginScreen = observer(() => {

    // other
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [otp, setOtp] = useState('');
    const [countdown, setCountdown] = useState(0); 
    const timerRef = useRef(null);
    // store
    const { authStore: { token, login, verifyOTP2FA, resendOTP2FA } } = useStore();
    // state
    const [ showPassword, setShowPassword ] = useState(false);
    const [ loginType, setLoginType ] = useState(LOGIN_TYPE.STUDENT);
    const [ emailCurrent, setEmailCurrent ] = useState('');
    const [ isResendLimitExceeded, setIsResendLimitExceeded ] = useState(false);
    const [ lockedTime, setLockedTime ] = useState(0);
    const [ timeLeft, setTimeLeft ] = useState(0); 
    const [headerHeight, setHeaderHeight] = useState(0);
    const { modalStore: { openAlert, show, hide } } = useStore();
    

    useEffect(() => {
        const header = document.getElementById('header-site');
        const linkLoginButton = document.getElementById('link-login-button');
        linkLoginButton.style.display = 'block';
        if (!header) return;

        const updateHeight = () => {
            setHeaderHeight(header.offsetHeight);
        };

        // Follow initial height header
        updateHeight();

        // Follow resize header
        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(header);

        // Follow resize window
        window.addEventListener('resize', updateHeight);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateHeight);
        };
    }, []);


    
    const formatCountDown = (totalSeconds) => {
        if (totalSeconds <= 0) return '';

        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = Math.floor(totalSeconds % 60);

        const pad = (n) => n.toString().padStart(2, '0');

        if (h > 0) return `${h}:${pad(m)}:${pad(s)}`; 
        return `${pad(m)}:${pad(s)}`;          
                                        
    };

   
    useEffect(() => {
        if (lockedTime > 0) {
            setTimeLeft(lockedTime);
        }
    }, [lockedTime]);

   
    useEffect(() => {
        if (timeLeft <= 0) return;

        const interval = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [timeLeft]);

    const validateLoginSchema = yup.object().shape({
        email: yup.string().required('メールアドレスを入力する必要があります。'),
        password: yup.string().required('パスワードを入力する必要があります。'),
        remember: yup.boolean()
        
    })

    const validateOTPSchema = yup.object().shape({
        otp: yup.string().required('OTPを入力する必要があります。').length(6, 'OTPは6桁で入力する必要があります。')
    })

    const defaultValues = { remember: true };

    const { register, handleSubmit, 
        formState: { errors, isSubmitting } } = useForm({resolver: yupResolver(validateLoginSchema), mode: 'onChange', defaultValues});

    useEffect(() => {
        if (countdown > 0) {
            timerRef.current = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
        } else {
            clearInterval(timerRef.current);
        }

        return () => clearInterval(timerRef.current);
    }, [countdown]);

    useEffect(() => {
        const linkLoginButton = document.getElementById('link-login-button');
        if (step === 2) {
            setCountdown(30);
            console.log(linkLoginButton);
            linkLoginButton.style.display = 'none';
        } else {
            linkLoginButton.style.display = 'block';
        }
        
    }, [step]);

    // function
    const toggleShowPassword = () => {
        setShowPassword(!showPassword);
    }

    const onLogin = async (data) => {
        const { remember, ...payload } = data;
        const fp = await FingerprintJS.load();
        const { visitorId } = await fp.get();
        
        const res = await flowResult(login({
            ...payload,
            fingerprintId: visitorId,
            role: loginType
        }, remember));
        
        if(res) {
            if (res.user?.needChangeEmail) {
                handleResignedConfirmModal(res.user, res);
            } else if (!res?.user?.is2FA) {
                setLockedTime(res?.lockedTime);
                setStep(2);
                // sessionStorage.setItem('isGetMessagePopup', true);
                // navigate(0);
            } else if (res?.user?.is2FA) {
                sessionStorage.setItem('isGetMessagePopup', true);
                const pending = consumeRedirect();
                if (pending) {
                    navigate(pending, { replace: true });
                } else {
                    navigate(0);
                }
            }
        } 
        setIsResendLimitExceeded(false);  
    }

    const onVerifyOTP2FA = async () => {
        const fp = await FingerprintJS.load();
        const { visitorId } = await fp.get();
        const res = await flowResult(verifyOTP2FA({ 
            otp,
            fingerprintId: visitorId
        }));
      
        if(res?.ok) {
            sessionStorage.setItem('isGetMessagePopup', true);
            const pending = consumeRedirect();
            if (pending) {
                navigate(pending, { replace: true });
            } else {
                navigate(0);
            }
        } else {
            if (res?.data?.lockedTime) {
                setLockedTime(res?.data?.lockedTime);
            }
        }
    }

    const onChangeLoginType = (loginType) => {
        setLoginType(loginType);
    }

    const onResendOTP = async () => {
       
        if (countdown > 0) return;

        const res = await resendOTP2FA();
        if(res?.ok) {
            // Reset OTP input
            setOtp('');
            // Reset countdown to 30s
            setCountdown(30);
        } else {
            setOtp('');
        }
        if (res?.data?.isResendLimitExceeded) {
            setIsResendLimitExceeded(res?.data?.isResendLimitExceeded)
            setOtp('');
           
        }
    }

    
    const handleResignedConfirmModal = (userInfo, originalData) => {
            
        show({
            id: 'modal-setting-resignation-for-ga ',
            isOpen: true,
            header: 'ご確認',
            children: (
                <div
                    className='wrapper main-student-list main-Attended-couse main-purchasse-history tool-certificate-screen pd-10'
                    style={{ minHeight: '200px'}}>
                    <div className='top-student-list'>
                        <ResignedConfirmModal isPopup userInfo={userInfo} hide={hide} setStep={setStep} originalData={originalData}/>
                    </div>
                </div>
            ),
            type: 'medium max-width-500',
            disabledCloseButton: true
        })
    }

    return(
        <div id='main' className='wrapper main-signin'>
            {step === 1 && (
               
                <div>
                    <div className="bres-cus" 
                        style={{ position: 'sticky', zIndex: 50, top: `${headerHeight}px`, left: '0', width: '100%', backgroundColor: '#FFF'}}>
                        <div className='container'>
                            <ul>
                                <li>
                                    <Link to={SYSTEM_PATH.HOME}><img src='/images/icon-home.png' alt='' /></Link> 
                                </li>
                                <li className='dash'>\</li>
                                <li>ログイン</li>
                            </ul>
                        </div>
                    </div>
                    <div className='content-signin'>
                        <div className='container'>
                            <div className='content-form'>
                                <h2 className='title-primary'>ログイン</h2>
                                <form onSubmit={handleSubmit(onLogin)}>
                                    <div className='change-role-tab'>
                                        <div 
                                            onClick={loginType === LOGIN_TYPE.GROUP_ADMIN ? () => onChangeLoginType(LOGIN_TYPE.STUDENT) : () => {}}
                                            className={
                                                classNames(
                                                    'd-flex align-items-center justify-content-center flex-gap-5 w-50 cursor-pointer',
                                                    loginType === LOGIN_TYPE.STUDENT && 'active')}>
                                            <i className='fa-solid fa-user'></i>
                                            <span>受講者</span>
                                        </div>
                                        <div
                                            onClick={loginType === LOGIN_TYPE.STUDENT ? () => onChangeLoginType(LOGIN_TYPE.GROUP_ADMIN) : () => {}}
                                            className={
                                                classNames(
                                                    'd-flex align-items-center justify-content-center flex-gap-5 w-50 cursor-pointer',
                                                    loginType === LOGIN_TYPE.GROUP_ADMIN && 'active')}>
                                            <i className='fa-solid fa-users-line'></i>
                                            <span>法人管理者</span>
                                        </div>
                                    </div>
                                    <div className='item item-1'>
                                        <label htmlFor='email'>メールアドレス <span>*</span></label>
                                        <input type='text' {...register('email', { onChange: (e) => setEmailCurrent(e.target.value) })}/>
                                        {
                                            errors?.email &&
                                    <div className='text-danger mg-t-5'>{errors.email?.message}</div>
                                        }
                                    </div>
                                    <div className='item'>
                                        <label htmlFor>パスワード <span>*</span></label>
                                        <div className='input'>
                                            <input type={!showPassword ? 'password' : 'text'} {...register('password')} 
                                                onCopy={(e)=> e.preventDefault()} 
                                                // onPaste={(e)=> e.preventDefault()}
                                                onCut={(e)=> e.preventDefault()}/>
                                            <button type='button' className='button-toggle-password' onClick={toggleShowPassword}>
                                                {
                                                    !showPassword ?
                                                        <i className='fa fa-eye-slash'></i>
                                                        : 
                                                        <i className='fa fa-eye'></i>
                                                }
                                            </button>
                                        </div>
                                        {
                                            errors?.password &&
                                    <div className='text-danger mg-t-5'>{errors.password?.message}</div>
                                        }
                                    </div>
                                    <div className='item item-checkbox justify-content-between flex-wrap flex-gap-10'>
                                        <span>
                                            <input type='checkbox' {...register('remember')}/>ログイン情報保存
                                        </span>
                                        <p className='note mg-tb-0'>
                                            <span onClick={() => navigate(SYSTEM_PATH.FORGOT_PASSWORD,
                                                { state: { loginType } })} className='hover-link'>パスワードを忘れた方はこちら </span>
                                        </p>
                                    </div>
                                    

                                    <input type='submit' value='ログイン'/>
                                    {
                                        loginType === LOGIN_TYPE.STUDENT && (
                                            <p className='note'>
                                                <span onClick={() => navigate(SYSTEM_PATH.REGISTRATION_FIRST_STEP)} className='hover-link'>
                                            新規登録がお済みでない方はこちら</span>
                                            </p>
                                        )
                                    }
                                    <p className={classNames(
                                        'note mg-t-0',
                                        loginType === LOGIN_TYPE.GROUP_ADMIN && 'mg-t-30')} onClick={() => navigate(SYSTEM_PATH.FAQ_SCREEN)}>
                                        {/* <i className='fa fa-question-circle' aria-hidden='true' /> */}
                                        <span className='hover-link'>ログインできない方はこちら</span>
                                    </p>
                                    {
                                        loginType === LOGIN_TYPE.GROUP_ADMIN && (
                                            <p style={{ height: '22px' }}></p>
                                        )
                                    }
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                  
                    <form 
                        className='container-otp'
                        onSubmit={(e) => {
                            e.preventDefault(); 
                            if (timeLeft <= 0 && otp.length === 6) {
                                onVerifyOTP2FA();
                            }
                        }}
                    >
                        <div style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <span className='text-title'>ワンタイムパスワード認証</span>
                            <span className='text-desc'>{emailCurrent}宛に送信されたワンタイムパスワードを入力してください。</span>
                        </div>
                        <div className='container-otp-input'>
                            <OtpInput
                                value={otp}
                                onChange={setOtp}
                                numInputs={6} 
                                renderSeparator={<span></span>} 
                                renderInput={(props) => <input {...props} disabled={timeLeft > 0}/>}
                                inputType="tel" 
                               
                                containerStyle={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: 'clamp(6px, 4px, 10px)'
                                }}
                                inputStyle={{
                                    width: 'clamp(40px, 12vw, 65px)',
                                    height: 'clamp(40px, 12vw, 50px)',
                                    fontSize: 'clamp(16px, 4vw, 20px)',
                                    borderRadius: '6px',
                                    border: '1px solid #ccc',
                                    textAlign: 'center'
                                }}
                            />
                        </div>
                        
                        {timeLeft > 0 ? (
                            <div className="text-danger" style={{ marginTop: '10px', textAlign: 'left' }}>
                                間違ったOTPコードを許容回数を超えて入力しました。<br />
                                <strong>{formatCountDown(timeLeft)}</strong> 後に再度お試しください。
                              
                            </div>
                        ): (
                            <div>
                                
                           
                                <span 
                                    onClick={() => onResendOTP()} 
                                    className={classNames('button-resend-otp', { 'disabled-link': countdown > 0 })}
                                    style={{
                                        cursor: countdown > 0 || isResendLimitExceeded  ? 'not-allowed' : 'pointer',
                                        color: countdown > 0 || isResendLimitExceeded ? '#6F6F6F' : '#007BFF',
                                        pointerEvents: countdown > 0 || isResendLimitExceeded ? 'none' : 'auto'
                                    }}
                                >
                                    {(countdown > 0 && !isResendLimitExceeded) ? `コードが届かない場合に再送信できるまで、あと${countdown}秒` : '再送信'}
                                </span>
                            </div>
                        )}
                        <div className='container-otp-button'>
                            <button 
                                type='button' 
                                onClick={() => {
                                    setStep(1);
                                    setOtp('');
                                    setCountdown(0); 
                                }}
                                className='container-otp-button-back'
                            >
                                戻る
                            </button>
                            
                           
                            <button 
                                type='submit' 
                                disabled={timeLeft > 0 || otp.length !== 6} 
                                style={{
                                    opacity: timeLeft > 0 || otp.length !== 6 ? 0.5 : 1,
                                    pointerEvents: timeLeft > 0 || otp.length !== 6 ? 'none' : 'auto'
                                }}
                                className='container-otp-button-confirm' 
                            >
                                確認
                            </button>
                            
                        </div>
                    </form>
                </div>
            )}

            {step === 3 && (
                <ChangeEmail 
                    setStep={setStep}
                    emailCurrent={emailCurrent}
                />
            )}
        </div>

    )
})

export default LoginScreen;